import { Link } from "react-router-dom";
import { FileText, ShieldCheck, LockKeyhole, BadgeHelp, Headphones, Mail, Globe } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { faqPage, legalLinks, legalPages } from "../lib/legalContent";
import { L, t } from "../lib/translations";

const iconMap   = { terms: FileText, warranty: ShieldCheck, privacy: LockKeyhole, faq: BadgeHelp };
const accentMap = {
  terms:    "#833AB4",
  warranty: "#F77737",
  privacy:  "#405DE6",
  faq:      "#E1306C",
};

const pick = (value, lang) => {
  if (Array.isArray(value)) return value;
  return value?.[lang] || value?.he || value?.en || "";
};

export function LegalPageLayout({ pageKey, children }) {
  const { lang } = useLanguage();
  const page   = pageKey === "faq" ? faqPage : legalPages[pageKey];
  const Icon   = iconMap[pageKey] || FileText;
  const accent = accentMap[pageKey] || accentMap.terms;

  if (!page) return null;

  const title    = pick(page.title,    lang);
  const subtitle = pick(page.subtitle, lang);
  const intro    = pick(page.intro,    lang);
  const updated  = pick(page.updatedAt, lang);

  return (
    <div className="min-h-screen pt-14 pb-20" data-testid={`${pageKey}-page`}>
      <div className="max-w-2xl mx-auto px-4 py-8">

        {/* ── Page header ── */}
        <div className="mb-6">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: `${accent}14`, border: `1px solid ${accent}28` }}>
              <Icon className="w-5 h-5" style={{ color: accent }} />
            </div>
            <div>
              <h1 className="text-2xl font-black font-outfit" style={{ color: "var(--text-primary)" }}>
                {title}
              </h1>
              <p className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>{subtitle}</p>
              {updated && (
                <p className="text-[11px] mt-1 font-medium" style={{ color: "var(--text-secondary)" }}>{updated}</p>
              )}
            </div>
          </div>

          {/* Nav pills */}
          <div className="flex flex-wrap gap-2">
            {legalLinks.map(item => {
              const active = item.key === pageKey;
              return (
                <Link key={item.key} to={item.path}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                  style={{
                    background: active ? `${accent}14` : "var(--surface-2)",
                    color:      active ? accent : "var(--text-secondary)",
                    border:     `1px solid ${active ? `${accent}30` : "var(--border-color)"}`,
                    transition: "background-color 160ms cubic-bezier(0.23,1,0.32,1), color 160ms cubic-bezier(0.23,1,0.32,1), border-color 160ms cubic-bezier(0.23,1,0.32,1)",
                  }}>
                  {pick(item.label, lang)}
                </Link>
              );
            })}
          </div>
        </div>

        {/* ── Intro cards ── */}
        {Array.isArray(intro) && intro.length > 0 && (
          <div className="grid gap-2.5 sm:grid-cols-2 mb-6">
            {intro.map((p, i) => (
              <div key={i} className="rounded-2xl border p-4 text-sm leading-relaxed"
                style={{ borderColor: "var(--border-color)", background: "var(--surface-1)", color: "var(--text-secondary)" }}>
                {p}
              </div>
            ))}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}

export function LegalSections({ pageKey }) {
  const { lang }  = useLanguage();
  const page      = legalPages[pageKey];
  const accent    = accentMap[pageKey] || accentMap.terms;

  if (!page) return null;

  return (
    <div className="space-y-2.5">
      {page.sections.map((section, index) => {
        const paragraphs = pick(section.paragraphs, lang);
        const bullets    = pick(section.bullets, lang);
        const title      = pick(section.title, lang);

        return (
          <div key={index} className="rounded-2xl border overflow-hidden"
            style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>

            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3.5 border-b"
              style={{ borderColor: "var(--border-color)" }}>
              <div className="w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black flex-shrink-0"
                style={{ background: `${accent}14`, color: accent, border: `1px solid ${accent}28` }}>
                {index + 1}
              </div>
              <h2 className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{title}</h2>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-4">
              {Array.isArray(paragraphs) && paragraphs.length > 0 && (
                <div className="space-y-2.5 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {paragraphs.map((p, pi) => <p key={pi}>{p}</p>)}
                </div>
              )}
              {Array.isArray(bullets) && bullets.length > 0 && (
                <ul className="space-y-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {bullets.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-2.5">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accent }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function FAQSections() {
  const { lang }    = useLanguage();
  const { user }    = useAuth();
  const navigate    = useNavigate();
  const accent      = accentMap.faq;
  

  const openSupport = () => {
    if (user) { window.dispatchEvent(new CustomEvent("open-support")); return; }
    navigate("/login?next=%2Fsupport");
  };

  return (
    <div className="space-y-2.5">
      {/* Accordion */}
      <div className="rounded-2xl border overflow-hidden"
        style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <Accordion type="single" collapsible className="w-full">
          {faqPage.items.map((item, index) => (
            <AccordionItem key={index} value={`faq-${index}`}
              className="border-b last:border-b-0 animate-fade-in-up"
              style={{ borderColor: "var(--border-color)", animationDelay: `${index * 40}ms` }}>
              <AccordionTrigger className="px-5 py-4 text-start text-sm font-semibold hover:no-underline gap-4"
                style={{ color: "var(--text-primary)" }}>
                <div className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-black flex-shrink-0"
                    style={{ background: `${accent}14`, color: accent }}>
                    {index + 1}
                  </span>
                  <span>{pick(item.question, lang)}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-4 pt-0">
                <div className="ps-8 space-y-2 text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                  {(pick(item.answer, lang) || []).map((p, pi) => <p key={pi}>{p}</p>)}
                  {item.link && (
                    <Link to={item.link.path}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold mt-1"
                      style={{ color: accent, transition: "opacity 160ms cubic-bezier(0.23,1,0.32,1)" }}>
                      {pick(item.link.label, lang)}
                    </Link>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>

      {/* Contact card */}
      <div className="rounded-2xl border p-5"
        style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${accent}14`, border: `1px solid ${accent}28` }}>
            <BadgeHelp className="w-4 h-4" style={{ color: accent }} />
          </div>
          <div>
            <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
              {pick(faqPage.contactCard.title, lang)}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              {pick(faqPage.contactCard.body, lang)}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <button onClick={openSupport}
            className="w-full h-10 rounded-xl font-semibold text-sm flex items-center justify-center gap-2"
            style={{ background: accent, color: "#fff", transition: "transform 160ms cubic-bezier(0.23,1,0.32,1), opacity 160ms cubic-bezier(0.23,1,0.32,1)" }}
            onMouseDown={e => e.currentTarget.style.transform = "scale(0.97)"}
            onMouseUp={e => e.currentTarget.style.transform = "scale(1)"}
            onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
            <Headphones className="w-4 h-4" />
            {t("c.LegalPageLayout.open_customer_service", lang)}
          </button>
          <a href={`mailto:${faqPage.contactCard.email}`}
            className="flex items-center gap-2.5 h-10 px-4 rounded-xl text-sm font-medium"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)", transition: "opacity 160ms cubic-bezier(0.23,1,0.32,1)" }}>
            <Mail className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{faqPage.contactCard.email}</span>
          </a>
          <a href={faqPage.contactCard.website} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2.5 h-10 px-4 rounded-xl text-sm font-medium"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)", transition: "opacity 160ms cubic-bezier(0.23,1,0.32,1)" }}>
            <Globe className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{faqPage.contactCard.website}</span>
          </a>
        </div>
      </div>
    </div>
  );
}
